import { useMemo } from 'react';

import { InformationalEvent } from '../types/api.types';

import introSectionStyles from '../../singleEvent/components/IntroSection/IntroSection.module.scss';
import Button from 'components/buttons/Button';
import Countdown from 'components/countdown/Countdown';
import { ReferralCountdownPart } from 'components/countdown/ReferralCountdownPart';
import { GlassBox } from 'components/glassBox/GlassBox';

import { DEFAULTS } from 'constants/defaults.constant';

import { useIsMobile } from 'hooks/useScreenSize';

import rewardsIcon from 'assets/single-event/rewards.svg';

import { formatDateTime } from '../helpers/date-format.helper';

type JoinInformationalProps = {
  informationalEvent: InformationalEvent | null;
  registrationEndDatetime: string;
  startDatetime: string;
};

export const JoinInformational = ({
  informationalEvent,
  registrationEndDatetime,
  startDatetime,
}: JoinInformationalProps) => {
  const isMobile = useIsMobile();

  const informationalLink = informationalEvent?.informational_link || '';
  const prize = informationalEvent?.informational_prize;

  const startTimestamp = useMemo(() => Math.floor(new Date(startDatetime).getTime() / 1000), [startDatetime]);
  const now = Math.floor(Date.now() / 1000);
  const isEventStarted = now >= startTimestamp;

  const formattedStartDate = useMemo(() => formatDateTime(startDatetime), [startDatetime]);
  const formattedRegistrationEndDate = useMemo(
    () => formatDateTime(registrationEndDatetime),
    [registrationEndDatetime]
  );

  const valueClass = isMobile
    ? 'tw-text-[42px] tw-font-bold tw-min-w-[60px] tw-h-[60px] tw-px-2'
    : 'tw-text-[42px] tw-font-bold tw-w-[60px] tw-h-[60px]';

  const showCountdown = !isEventStarted;

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[20px] tw-px-[20px] lg:tw-px-0">
      <h2 className="tw-font-jost tw-text-[40px] tw-font-bold tw-leading-[120%] tw-text-white">
        Event Details
      </h2>
      <div className="tw-font-jost tw-text-[12px] tw-leading-[20px] tw-text-white lg:tw-text-[15px]">
        {isEventStarted ? 'Event is live!' : `Starting on ${formattedStartDate}`}
      </div>

      {informationalLink && (
        <GlassBox
          gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
          borderWidth="1px"
          className="tw-flex tw-w-full tw-flex-row tw-items-center tw-justify-center tw-gap-[20px] tw-p-[10px] lg:tw-max-w-[550px]"
        >
          <span className="tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-left tw-text-[15px] tw-font-bold tw-text-[#20AA67]">
            {informationalLink}
          </span>
          <Button
            text="Visit"
            size="medium-small"
            color="referral-cta"
            className="tw-w-[120px] tw-min-w-[120px] lg:tw-w-[120px] lg:tw-min-w-[100px]"
            contentClassName="tw-text-[15px] lg:tw-text-[18px] tw-normal-case tw-font-semibold"
            onClick={() => window.open(informationalLink, '_blank', 'noopener,noreferrer')}
          />
        </GlassBox>
      )}

      {prize && (
        <GlassBox
          gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
          borderWidth="1px"
          className="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-[10px] tw-p-[25px] lg:tw-max-w-[550px]"
        >
          <img src={rewardsIcon} alt="Prize" className="tw-h-[100px] tw-w-[100px]" />
          <span className="tw-font-jost tw-text-[20px] tw-font-bold tw-uppercase tw-leading-[20px] tw-text-[#1FB563]">
            Rewards:
          </span>
          <p className="tw-m-0 tw-font-jost tw-text-[20px] tw-font-semibold tw-leading-[20px] tw-text-white">
            {prize.name || 'Prize'}
          </p>
          <p className="tw-p-0 tw-font-jost tw-text-[15px] tw-leading-[20px] tw-text-white">
            *available only to users who registered before {formattedRegistrationEndDate}
          </p>
        </GlassBox>
      )}

      {showCountdown && (
        <Countdown
          label="Event Starts In:"
          endTimestamp={startTimestamp * DEFAULTS.tradeTimestampMultiplier}
          labelClass={['tw-text-white tw-text-[20px] tw-font-bold tw-leading-[24px] tw-mb-[16px] tw-capitalize']}
          valueClass={[valueClass, '!tw-text-[36px] tw-font-bold']}
          containerClass={[introSectionStyles['intro-section-countdown-part-container']]}
          labelsClass={['tw-uppercase tw-font-medium tw-text-[12px] lg:tw-text-[15px] tw-mt-[8px] tw-text-[#20AA67]']}
          valueWrapperClass={['tw-w-[62px] tw-h-[62px]']}
          CountdownPartComponent={ReferralCountdownPart}
        />
      )}
    </div>
  );
};
